import { useEffect, useRef, useState } from "react";
import { Camera } from "lucide-react";

// A profile picture: an uploaded photo, one of a fixed set of theatre emoji, or the username's first letter.
export const AVATARS = ["🎭", "🎟️", "🎩", "🌹", "🎻", "🎷", "🎤", "🕯️", "👑", "🦚", "🌟", "🎪", "🩰", "🥂", "🎬", "🪭"];

const SIZES = { sm: "h-9 w-9 text-lg", md: "h-11 w-11 text-xl", lg: "h-20 w-20 text-4xl sm:h-24 sm:w-24 sm:text-5xl" };

export function Avatar({ avatar, url, name = "?", size = "md" }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);     // a new link (for example after a refresh) gets a fresh try
  if (url && !broken) {
    return <img src={url} alt="" onError={() => setBroken(true)} data-testid="avatar" data-photo="1"
      className={`flex-shrink-0 rounded-full border-2 border-[#C89B3C] bg-[#2a1620] object-cover ${SIZES[size] || SIZES.md}`} />;
  }
  return (
    <span className={`flex flex-shrink-0 items-center justify-center rounded-full ${avatar ? "border-2 border-[#C89B3C] bg-[#2a1620]" : "bg-[#C89B3C] font-serif font-bold text-black"} ${SIZES[size] || SIZES.md}`} aria-hidden="true" data-testid="avatar">
      {avatar || (name[0] || "?").toUpperCase()}
    </span>
  );
}

/** Pick an emoji avatar or upload a photo.
 *  photoUrl: the current uploaded picture, if any. onPhoto(file) is called with a chosen file; onRemovePhoto() clears it. */
export function AvatarPicker({ value, onChange, photoUrl = "", onPhoto, onRemovePhoto, uploading = false }) {
  const input = useRef(null);
  return (
    <div data-testid="avatar-picker">
      {onPhoto && (
        <div className="mb-3 flex flex-wrap items-center gap-3">
          {photoUrl && <img src={photoUrl} alt="Your profile picture" className="h-14 w-14 rounded-full border-2 border-[#C89B3C] object-cover" data-testid="avatar-photo-preview" />}
          <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-[#C89B3C]/60 px-4 py-2 text-sm font-medium text-[#E6C071] ${uploading ? "opacity-60" : ""}`}>
            <Camera className="h-4 w-4" aria-hidden="true" />
            {uploading ? "Uploading…" : photoUrl ? "Change photo" : "Upload a photo"}
            <input ref={input} type="file" accept="image/*" className="sr-only" disabled={uploading} data-testid="avatar-photo-input"
              onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) onPhoto(f); }} />
          </label>
          {photoUrl && <button type="button" onClick={onRemovePhoto} data-testid="avatar-photo-remove" className="text-sm text-muted-foreground underline">Remove photo</button>}
        </div>
      )}
      <div role="radiogroup" aria-label="Choose an avatar" className="flex flex-wrap gap-2">
        <button type="button" role="radio" aria-checked={!value && !photoUrl} onClick={() => onChange("")} data-testid="avatar-none"
          className={`rounded-full px-3 py-1.5 text-sm ${!value && !photoUrl ? "bg-[#C89B3C] text-black" : "border border-border text-muted-foreground"}`}>Initial</button>
        {AVATARS.map((a) => (
          <button key={a} type="button" role="radio" aria-checked={value === a} aria-label={`Avatar ${a}`} onClick={() => onChange(a)} data-testid="avatar-option"
            className={`flex h-11 w-11 items-center justify-center rounded-full text-2xl ${value === a ? "border-2 border-[#C89B3C] bg-[#C89B3C]/20" : "border border-border"}`}>{a}</button>
        ))}
      </div>
    </div>
  );
}